/** Firestore operations for Ready to Bill items (see readyToBill.ts for the model). */

import { db } from './firebase';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
  deleteDoc,
  updateDoc,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import type { BillingReadyItem, BillingReadyStatus, ReadyToBillRow } from './readyToBill';
import type { SalesOrderInstallmentId } from '../types';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const ITEMS = 'billingReadyItems';

function toDate(v: unknown): Date | undefined {
  if (!v) return undefined;
  if (v instanceof Date) return v;
  if (typeof (v as { toDate?: () => Date }).toDate === 'function') {
    return (v as { toDate: () => Date }).toDate();
  }
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function mapItem(snap: { id: string; data: () => Record<string, unknown> }): BillingReadyItem {
  const d = snap.data();
  return {
    id: snap.id,
    dealId: String(d.dealId || ''),
    salesOrderId: typeof d.salesOrderId === 'string' ? d.salesOrderId : undefined,
    installmentId: typeof d.installmentId === 'string' ? d.installmentId : undefined,
    kind: (d.kind as BillingReadyItem['kind']) || 'custom',
    label: String(d.label || ''),
    detail: typeof d.detail === 'string' ? d.detail : undefined,
    amount: typeof d.amount === 'number' ? d.amount : 0,
    status: (d.status as BillingReadyStatus) || 'ready',
    notes: typeof d.notes === 'string' ? d.notes : undefined,
    quickbooksInvoiceNum:
      typeof d.quickbooksInvoiceNum === 'string' ? d.quickbooksInvoiceNum : undefined,
    markedReadyByUserId: String(d.markedReadyByUserId || ''),
    markedReadyByUserName: String(d.markedReadyByUserName || ''),
    markedReadyAt: toDate(d.markedReadyAt) ?? new Date(),
    updatedByUserId: typeof d.updatedByUserId === 'string' ? d.updatedByUserId : undefined,
    updatedByUserName: typeof d.updatedByUserName === 'string' ? d.updatedByUserName : undefined,
    updatedAt: toDate(d.updatedAt),
  };
}

export async function markReadyToBill(args: {
  row: Pick<ReadyToBillRow, 'dealId' | 'label' | 'detail' | 'amount' | 'kind'> & {
    salesOrderId?: string;
    installmentId?: string;
    changeOrderId?: string;
  };
  notes?: string;
  byUserId: string;
  byUserName: string;
  status?: BillingReadyStatus;
  quickbooksInvoiceNum?: string;
}): Promise<string> {
  const now = Timestamp.now();
  const installmentId =
    args.row.kind === 'change_order' && args.row.changeOrderId
      ? `co-${args.row.changeOrderId}`
      : args.row.installmentId;
  const refDoc = await addDoc(
    collection(db, ITEMS),
    removeUndefinedDeep({
      dealId: args.row.dealId,
      salesOrderId: args.row.salesOrderId,
      installmentId,
      kind: args.row.kind,
      label: args.row.label,
      detail: args.row.detail,
      amount: Math.round(Number(args.row.amount) || 0),
      status: args.status || 'ready',
      notes: args.notes,
      quickbooksInvoiceNum: args.quickbooksInvoiceNum,
      markedReadyByUserId: args.byUserId,
      markedReadyByUserName: args.byUserName,
      markedReadyAt: now,
      updatedAt: now,
    }),
  );
  return refDoc.id;
}

/** Drop a Ready to bill mark so the installment returns to the forecast. */
export async function deleteBillingReadyItem(itemId: string): Promise<void> {
  await deleteDoc(doc(db, ITEMS, itemId));
}

export async function updateBillingReadyItemStatus(args: {
  itemId: string;
  status: BillingReadyStatus;
  byUserId: string;
  byUserName: string;
  quickbooksInvoiceNum?: string;
  notes?: string;
}): Promise<void> {
  const payload: Record<string, unknown> = {
    status: args.status,
    updatedByUserId: args.byUserId,
    updatedByUserName: args.byUserName,
    updatedAt: Timestamp.now(),
  };
  if (args.quickbooksInvoiceNum !== undefined) {
    payload.quickbooksInvoiceNum = args.quickbooksInvoiceNum.trim();
  }
  if (args.notes !== undefined) payload.notes = args.notes.trim();
  await updateDoc(doc(db, ITEMS, args.itemId), payload as DocumentData);
}

const PROGRESS_INSTALLMENTS = new Set<SalesOrderInstallmentId>([
  'deposit',
  'pre_shipment',
  'final',
]);

/**
 * Persist a Billing & Payables "Paid" mark onto the sales-order installment
 * so Forecast / QB flags treat it as collected even if the QuickBooks mirror
 * is still open or the tracking doc fails to attach.
 */
export async function markSalesOrderInstallmentCollected(args: {
  salesOrderId?: string;
  installmentId?: string;
  quickbooksInvoiceId?: string;
  quickbooksInvoiceNum?: string;
}): Promise<void> {
  const { salesOrderId, installmentId } = args;
  if (!salesOrderId || !installmentId) return;
  if (!PROGRESS_INSTALLMENTS.has(installmentId as SalesOrderInstallmentId)) return;

  const ref = doc(db, 'salesOrders', salesOrderId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  const invoices = Array.isArray(snap.data().installmentInvoices)
    ? [...(snap.data().installmentInvoices as Array<Record<string, unknown>>)]
    : [];
  const candidates = invoices
    .map((entry, index) => ({ entry, index }))
    .filter((item) => item.entry?.installmentId === installmentId && item.entry.status !== 'voided' && item.entry.status !== 'removed_in_qb');
  const byInvoice = args.quickbooksInvoiceId
    ? candidates.find((item) => item.entry.quickbooksInvoiceId === args.quickbooksInvoiceId)
    : args.quickbooksInvoiceNum
      ? candidates.find((item) => item.entry.quickbooksInvoiceNum === args.quickbooksInvoiceNum)
      : undefined;
  const fullOnly = candidates.filter((item) => !item.entry.partialId && !(Array.isArray(item.entry.structureIds) && item.entry.structureIds.length));
  const idx = byInvoice?.index ?? (candidates.length === 1 ? candidates[0].index : fullOnly.length === 1 ? fullOnly[0].index : -1);
  if (idx < 0) return;

  const entry = invoices[idx]!;
  if (entry.status === 'paid' || entry.status === 'voided') return;

  invoices[idx] = {
    ...entry,
    status: 'paid',
    balance: 0,
    paidAt: entry.paidAt ?? Timestamp.now(),
  };
  await updateDoc(ref, { installmentInvoices: invoices });
}

export function subscribeBillingReadyItemsForDeal(
  dealId: string,
  cb: (items: BillingReadyItem[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const q = query(collection(db, ITEMS), where('dealId', '==', dealId));
  return onSnapshot(q, (snap) => cb(snap.docs.map(mapItem)), onError);
}

export function subscribeBillingReadyItems(
  cb: (items: BillingReadyItem[]) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const q = query(collection(db, ITEMS), orderBy('markedReadyAt', 'desc'));
  return onSnapshot(q, (snap) => cb(snap.docs.map(mapItem)), onError);
}
